import React from "react";
import styled from "styled-components";
import { Github, Linkedin, Mail, Heart } from "lucide-react";

const FooterWrapper = styled.footer`
  background-color: ${props => props.theme.colors.surface};
  border-top: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  padding: 40px 0 24px;
  margin-top: 60px;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 32px;
  margin-bottom: 24px;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-family: ${props => props.theme.fonts.primary};
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${props => props.theme.colors.textPrimary};
  }

  p {
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.6;
    margin-bottom: 12px;
    font-size: 14px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      margin-bottom: 8px;

      a {
        color: ${props => props.theme.colors.textSecondary};
        text-decoration: none;
        font-size: 14px;
        transition: all 0.15s ease;

        &:hover {
          color: ${props => props.theme.colors.primary};
        }
      }
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.surfaceSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.hover};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${props => props.theme.colors.border};
  padding-top: 24px;
  text-align: center;
`;

const Copyright = styled.p`
  color: ${props => props.theme.colors.textTertiary};
  font-size: 12px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
    color: ${props => props.theme.colors.primary};
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      href: "https://github.com/hassan8537",
      icon: Github,
      label: "GitHub",
    },
    {
      href: "https://www.linkedin.com/in/m-hassan-ahmed-976903220",
      icon: Linkedin,
      label: "LinkedIn",
    },
    {
      href: "mailto:hassan.m.8537@gmail.com",
      icon: Mail,
      label: "Email",
    },
  ];

  return (
    <FooterWrapper>
      <FooterContent>
        <FooterSection>
          <h3>M Hassan Ahmed</h3>
          <p>
            Software Engineer specializing in cloud-native development and
            scalable solutions. Passionate about AWS, Node.js, and modern web
            technologies.
          </p>
          <SocialLinks>
            {socialLinks.map(({ href, icon: Icon, label }) => (
              <SocialLink
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={label}
              >
                <Icon />
              </SocialLink>
            ))}
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <h3>Quick Links</h3>
          <ul>
            <li>
              <a href="/">Home</a>
            </li>
            <li>
              <a href="/about">About</a>
            </li>
            <li>
              <a href="/projects">Projects</a>
            </li>
            <li>
              <a href="/contact">Contact</a>
            </li>
          </ul>
        </FooterSection>

        <FooterSection>
          <h3>Technologies</h3>
          <ul>
            <li>Node.js & Python</li>
            <li>AWS Cloud Services</li>
            <li>React.js & Modern Frontend</li>
            <li>AI/ML Integration</li>
          </ul>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <Copyright>
          © {currentYear} M Hassan Ahmed. Made with <Heart /> using AWS design
          principles.
        </Copyright>
      </FooterBottom>
    </FooterWrapper>
  );
};

export default Footer;
